import React, { useCallback, useContext, useEffect, useState } from 'react';

import { EventProviderProps, IEventContext } from './interfaces';
import { useMytaverse } from '@providers/MytaverseProvider';
import { useNotificationContext } from '@providers/NotificationProvider';
import ParticipantsService from '@services/ParticipantsService';
import { IUser } from '@interfaces/user';
import { useAssetStore } from '@components/AssetPreloader/assetsCasheContext';
import { getStorageValue, removeLocalStorageValue } from '@helpers/storage';
import { StorageTypes, StorageValues } from '@interfaces/storage';
import { useLocation, useParams } from 'react-router-dom';
import { IProfileState } from '@interfaces/profile';
import ROUTES from '@constants/routes';

export const EventContext = React.createContext<IEventContext>(
  {} as IEventContext,
);

export const useEventContext = () => useContext(EventContext);

export const EventProvider: React.FC<EventProviderProps> = ({ children }) => {
  const { cachedAssets, cleanCachedAsset } = useAssetStore();

  const { eventId: currentEventId } = useParams();
  const { pathname } = useLocation();

  const { user, setUser, setCustomAvatarUrl, setCurrentAvatarId } =
    useMytaverse();

  const [avatarConsentOpen, setAvatarConsentOpen] = useState(false);

  const [isEventRun, setIsEventRun] = useState(false);

  const { setIsEventRun: setIsEnterWorld } = useNotificationContext();

  const [launchArgs, setLaunchArgs] = useState<string[] | null>(null);

  const updateProfileState = useCallback(async (payload: IProfileState) => {
    if (!payload) {
      return;
    }

    try {
      const { state } =
        await ParticipantsService.updateCurrentParticipantProfileState(payload);

      if (state) {
        setUser((prevState: IUser) => ({ ...prevState, state }));
      }
    } catch (e) {
      console.log('Cannot update participant state');
    }
  }, []);

  useEffect(() => {
    setIsEnterWorld(isEventRun);
  }, [isEventRun]);

  useEffect(() => {
    if (isEventRun && user?.id && currentEventId) {
      updateProfileState({
        lastEntranceTimestamp: { [currentEventId]: Date.now() },
        ...(!user?.state?.deviceCheckedTimestamp
          ? { deviceCheckedTimestamp: Date.now() }
          : {}),
      });
    }
  }, [isEventRun, user?.id, currentEventId]);

  useEffect(() => {
    if (isEventRun && Object.entries(cachedAssets).length) {
      Object.entries(cachedAssets).map(([, blobUrl]) => {
        URL.revokeObjectURL(blobUrl);
      });
      cleanCachedAsset();
    }
  }, [isEventRun, cachedAssets]);

  useEffect(() => {
    let extraLaunchArgs: any = getStorageValue(
      StorageValues.ExtraLaunchArgs,
      StorageTypes.Session,
    );

    try {
      extraLaunchArgs = JSON.parse(extraLaunchArgs);
    } catch (e) {
      console.log('Cannot parse extra launch args');
    }

    if (
      extraLaunchArgs &&
      extraLaunchArgs.eventId === currentEventId &&
      extraLaunchArgs.launchArgs
    ) {
      setLaunchArgs(extraLaunchArgs.launchArgs);
    }
  }, []);

  useEffect(() => {
    if (isEventRun) {
      removeLocalStorageValue(
        StorageValues.ExtraLaunchArgs,
        StorageTypes.Session,
      );
    }
  }, [isEventRun]);

  useEffect(() => {
    if (pathname === ROUTES.EVENT_PAGE(currentEventId)) {
      // Should be run only after refresh of an experience
      setCustomAvatarUrl((prev) => {
        if (prev || !user.defaultAvatar) {
          return prev;
        }

        if (user.defaultAvatar?.avatarUrl) {
          const avatarId = user.defaultAvatar?.avatarId
            ? user.defaultAvatar?.avatarId
            : null;
          setCurrentAvatarId(avatarId);
          setUser((prevState: IUser) => ({
            ...prevState,
            activeAvatarId: avatarId,
          }));

          return user.defaultAvatar?.avatarUrl;
        }

        return prev;
      });
    }
  }, [currentEventId, user?.defaultAvatar]);

  return (
    <EventContext.Provider
      value={{
        isEventRun,
        setIsEventRun,
        avatarConsentOpen,
        setAvatarConsentOpen,
        launchArgs,
        setLaunchArgs,
      }}
    >
      {children}
    </EventContext.Provider>
  );
};
